import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    // Check if this is an expected 401 error during initial auth check
    const isExpected401 = error?.message?.includes('Unauthorized - expected during initial auth check') ||
                         error?.message?.includes('Request failed with status code 401') ||
                         (error as any)?.response?.status === 401 ||
                         (error as any)?.isExpected === true;
    
    // If it's an expected 401, don't show error boundary
    if (isExpected401) {
      return { hasError: false, error: null };
    }
    
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Check if this is an expected 401 error
    const isExpected401 = error?.message?.includes('Unauthorized - expected during initial auth check') ||
                         error?.message?.includes('Request failed with status code 401') ||
                         (error as any)?.response?.status === 401 ||
                         (error as any)?.isExpected === true;
    
    // Don't log expected 401 errors
    if (!isExpected401) {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      // Only show error UI for unexpected errors
      return (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h2>Something went wrong.</h2>
          <p>Please refresh the page.</p>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;

